import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import type {
  OrgInvitationRole,
  OrgInvitationSummary,
  OrgMemberSummary,
} from '@nadoutong/contracts';
import { localizeCaughtError } from '../../api/errorMessages.ts';
import { useOrgInvitations, useOrgMembers, useRevokeOrgInvitation } from '../../api/useOrgs.ts';
import { InviteLinkModal, InviteMemberModal } from '../../components/InviteMemberDialog.tsx';
import { PanelError } from '../../components/PanelError.tsx';
import { Skeleton } from '../../components/Skeleton.tsx';
import { StatusChip } from '../../components/StatusChip.tsx';
import { useToast } from '../../components/Toast.tsx';
import { formatTimestamp } from '../../components/formatters.ts';
import { hasPermission, useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, ConfirmDialog, Panel, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';
import { ModelPermissionsModal } from '../../components/ModelPermissionsModal.tsx';

/**
 * Org members card for `/settings` — Field Notes rebuild (epic #950
 * PR-4) of the console-shared Settings factory's members section:
 * member roster, pending invitations (invite / revoke), per-member
 * model-permission editor entry.
 */

type TranslateFn = ReturnType<typeof useT>;

function memberRoleLabel(t: TranslateFn, role: OrgMemberSummary['role']): string {
  const key: I18nKey =
    role === 'owner'
      ? 'settings.members.role.owner'
      : role === 'admin'
        ? 'settings.members.role.admin'
        : role === 'finance'
          ? 'settings.members.role.finance'
          : 'settings.members.role.member';
  return t(key);
}

function invitedRoleLabel(t: TranslateFn, role: OrgInvitationRole): string {
  if (role === 'admin') return t('settings.invite_modal.role.admin');
  if (role === 'finance') return t('settings.invite_modal.role.finance');
  return t('settings.invite_modal.role.member');
}

export function MembersCard(): JSX.Element {
  const { user } = useSession();
  const t = useT();
  const orgId = user?.orgId ?? undefined;
  const membersQ = useOrgMembers(orgId);
  const invitationsQ = useOrgInvitations(orgId);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  const [modelPermissionsMember, setModelPermissionsMember] = useState<OrgMemberSummary | null>(
    null,
  );

  const canInvite = hasPermission(user, 'org:invitations:manage');
  const canManageModelPermissions = hasPermission(user, 'org:members:manage');

  return (
    <Panel
      caption={t('settings.members.title')}
      padded
      testId="settings-members-card"
      actions={
        canInvite ? (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setInviteOpen(true)}
            data-testid="members-invite-button"
          >
            <UserPlus aria-hidden="true" className="h-3.5 w-3.5" />
            {t('settings.members.invite_button')}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-5">
        <p className="m-0 text-caption text-ink-low">{t('settings.members.subtitle')}</p>

        {membersQ.isLoading ? (
          <div className="flex flex-col gap-2" data-testid="members-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
            <span className="sr-only">{t('settings.members.loading')}</span>
          </div>
        ) : membersQ.isError ? (
          <PanelError
            message={localizeCaughtError(membersQ.error, t, 'settings.members.error')}
            retryLabel={t('settings.retry')}
            onRetry={() => {
              void membersQ.refetch();
            }}
            testId="members-error"
          />
        ) : membersQ.data !== undefined ? (
          <MembersTable
            members={membersQ.data.members}
            canManageModelPermissions={canManageModelPermissions}
            onEditModelPermissions={(member) => setModelPermissionsMember(member)}
          />
        ) : null}

        {canInvite ? <InvitationsSection orgId={orgId} query={invitationsQ} /> : null}
      </div>

      {inviteOpen && orgId !== undefined ? (
        <InviteMemberModal
          orgId={orgId}
          onClose={() => setInviteOpen(false)}
          onCreated={(url) => {
            setInviteOpen(false);
            setLastInviteUrl(url);
          }}
        />
      ) : null}

      {lastInviteUrl !== null ? (
        <InviteLinkModal url={lastInviteUrl} onClose={() => setLastInviteUrl(null)} />
      ) : null}

      {modelPermissionsMember !== null && orgId !== undefined ? (
        <ModelPermissionsModal
          orgId={orgId}
          target={{ kind: 'member', member: modelPermissionsMember }}
          onClose={() => setModelPermissionsMember(null)}
        />
      ) : null}
    </Panel>
  );
}

interface MembersTableProps {
  readonly members: readonly OrgMemberSummary[];
  readonly canManageModelPermissions: boolean;
  readonly onEditModelPermissions: (member: OrgMemberSummary) => void;
}

function MembersTable({
  members,
  canManageModelPermissions,
  onEditModelPermissions,
}: MembersTableProps): JSX.Element {
  const t = useT();
  if (members.length === 0) {
    return (
      <p className="m-0 text-body text-ink-low" data-testid="members-empty">
        {t('settings.members.empty')}
      </p>
    );
  }
  return (
    <div data-testid="members-table">
      <Table>
        <THead>
          <Tr interactive={false}>
            <Th>{t('settings.members.column.email')}</Th>
            <Th>{t('settings.members.column.role')}</Th>
            <Th>{t('settings.members.column.joined')}</Th>
            {canManageModelPermissions ? (
              <Th className="text-right">{t('settings.members.column.actions')}</Th>
            ) : null}
          </Tr>
        </THead>
        <TBody>
          {members.map((m) => (
            <Tr key={m.accountId} data-testid={`members-row-${m.accountId}`} interactive={false}>
              <Td mono heading>
                {m.email}
              </Td>
              <Td label={t('settings.members.column.role')}>
                <StatusChip
                  variant={m.role === 'owner' ? 'ok' : 'neutral'}
                  label={memberRoleLabel(t, m.role)}
                />
              </Td>
              <Td mono label={t('settings.members.column.joined')}>
                {formatTimestamp(m.joinedAt)}
              </Td>
              {canManageModelPermissions ? (
                <Td className="text-right" label={t('settings.members.column.actions')}>
                  {m.role === 'member' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEditModelPermissions(m)}
                      data-testid={`members-model-permissions-${m.accountId}`}
                    >
                      {t('settings.members.model_permissions')}
                    </Button>
                  ) : (
                    <span className="font-mono text-mono-label text-ink-low">
                      {t('settings.members.unrestricted')}
                    </span>
                  )}
                </Td>
              ) : null}
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

interface InvitationsSectionProps {
  readonly orgId: string | undefined;
  readonly query: ReturnType<typeof useOrgInvitations>;
}

function InvitationsSection({ orgId, query }: InvitationsSectionProps): JSX.Element {
  const t = useT();
  return (
    <section className="flex flex-col gap-3" data-testid="invitations-section">
      <header>
        <h3 className="m-0 text-label text-ink-mid">{t('settings.invitations.title')}</h3>
      </header>
      {query.isLoading ? (
        <Skeleton className="h-10" />
      ) : query.isError ? (
        <PanelError
          message={localizeCaughtError(query.error, t, 'settings.invitations.error')}
          retryLabel={t('settings.retry')}
          onRetry={() => {
            void query.refetch();
          }}
          testId="invitations-error"
        />
      ) : query.data !== undefined ? (
        query.data.invitations.length === 0 ? (
          <p className="m-0 text-body text-ink-low" data-testid="invitations-empty">
            {t('settings.invitations.empty')}
          </p>
        ) : (
          <InvitationsTable orgId={orgId ?? ''} invitations={query.data.invitations} />
        )
      ) : null}
    </section>
  );
}

interface InvitationsTableProps {
  readonly orgId: string;
  readonly invitations: readonly OrgInvitationSummary[];
}

/**
 * Invitation revoke runs through the same danger `ConfirmDialog` (and
 * the same catalog entries) as the `/organization` invitations panel —
 * this used to mutate on one click, the exact bug the panel's port
 * from console-web records as fixed. A rejected revoke reports inline
 * in the dialog and keeps it open; only success toasts.
 */
function InvitationsTable({ orgId, invitations }: InvitationsTableProps): JSX.Element {
  const t = useT();
  const revokeM = useRevokeOrgInvitation();
  const toast = useToast();
  const [revokeTarget, setRevokeTarget] = useState<OrgInvitationSummary | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  async function handleRevoke(): Promise<void> {
    if (revokeTarget === null) return;
    setRevokeError(null);
    try {
      await revokeM.mutateAsync({ orgId, invitationId: revokeTarget.id });
      setRevokeTarget(null);
      toast.success(t('organization.invitations.toast.revoked'));
    } catch (err) {
      setRevokeError(localizeCaughtError(err, t, 'organization.invitations.revoke_error'));
    }
  }

  return (
    <div data-testid="invitations-table">
      <Table>
        <THead>
          <Tr interactive={false}>
            <Th>{t('settings.invitations.column.email')}</Th>
            <Th>{t('settings.invitations.column.role')}</Th>
            <Th>{t('settings.invitations.column.invited_by')}</Th>
            <Th>{t('settings.invitations.column.expires')}</Th>
            <Th className="text-right">{t('settings.invitations.column.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {invitations.map((inv) => (
            <Tr key={inv.id} data-testid={`invitations-row-${inv.id}`} interactive={false}>
              <Td mono heading>
                {inv.email}
              </Td>
              <Td label={t('settings.invitations.column.role')}>{invitedRoleLabel(t, inv.role)}</Td>
              <Td mono label={t('settings.invitations.column.invited_by')}>
                {inv.invitedByEmail ?? inv.invitedByAccountId}
              </Td>
              <Td mono label={t('settings.invitations.column.expires')}>
                {formatTimestamp(inv.expiresAt)}
              </Td>
              <Td className="text-right" label={t('settings.invitations.column.actions')}>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setRevokeError(null);
                      setRevokeTarget(inv);
                    }}
                    disabled={revokeM.isPending}
                    data-testid={`invitations-revoke-${inv.id}`}
                  >
                    {t('settings.invitations.revoke')}
                  </Button>
                </div>
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      {revokeTarget !== null ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !revokeM.isPending) {
              setRevokeTarget(null);
              setRevokeError(null);
            }
          }}
          tone="danger"
          title={t('organization.invitations.revoke_title')}
          body={
            <div className="flex flex-col gap-2">
              <p className="m-0">
                {t('organization.invitations.revoke_body', { email: revokeTarget.email })}
              </p>
              {revokeError !== null ? (
                <p
                  role="alert"
                  data-testid="settings-invitations-revoke-error"
                  className="m-0 text-caption text-bad"
                >
                  {revokeError}
                </p>
              ) : null}
            </div>
          }
          confirmLabel={t('organization.invitations.revoke_confirm')}
          cancelLabel={t('organization.action.cancel')}
          closeLabel={t('organization.invitations.revoke_close')}
          loading={revokeM.isPending}
          testId="settings-invitation-revoke-dialog"
          onConfirm={() => {
            void handleRevoke();
          }}
        />
      ) : null}
    </div>
  );
}
